import { defineStore } from 'pinia'
import api from '@/api/axios'
import { ref } from 'vue'

export const useProjectStore = defineStore('project', () => {
  const projects = ref([])
  const selectedProjectId = ref(null)
  const selectedProject = ref(null)
  const loading = ref(false)
  const error = ref(null)

  async function fetchProjects() {
    loading.value = true
    error.value = null
    try {
      const res = await api.get('/projects')
      projects.value = res.data.projects || res.data
      return projects.value
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to fetch projects'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function createProject(payload) {
    loading.value = true
    error.value = null
    try {
      const res = await api.post('/projects', payload)
      projects.value.push(res.data.project)
      return res.data.project
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create project'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function getProjectDetails(projectId) {
    loading.value = true
    error.value = null
    try {
      const res = await api.get(`/projects/${projectId}`)
      selectedProject.value = res.data.project || res.data
      return selectedProject.value
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to get project details'
      throw err
    } finally {
      loading.value = false
    }
  }

  function selectProject(projectId) {
    selectedProjectId.value = projectId
    selectedProject.value = projects.value.find(p => p._id === projectId) || null
  }

  async function updateProject(projectId, payload) {
    loading.value = true
    error.value = null
    try {
      const res = await api.patch(`/projects/${projectId}`, payload)
      const idx = projects.value.findIndex(p => p._id === projectId)
      if (idx !== -1) projects.value[idx] = res.data.project
      if (selectedProjectId.value === projectId) selectedProject.value = res.data.project
      return res.data.project
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update project'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function deleteProject(projectId, options = {}) {
    loading.value = true
    error.value = null
    try {
      const { deleteType = 'soft', retentionDays } = options
      const payload = { deleteType }
      if (retentionDays && deleteType === 'scheduled') {
        payload.retentionDays = retentionDays
      }
      
      const response = await api.delete(`/projects/${projectId}`, { data: payload })
      
      // For soft delete, project stays in list but marked as deleted
      // For hard delete, remove from list entirely
      if (deleteType === 'hard') {
        projects.value = projects.value.filter(p => p._id !== projectId)
        if (selectedProjectId.value === projectId) {
          selectedProjectId.value = null
          selectedProject.value = null
        }
      } else {
        // Update project to show as deleted/archived
        const projectIndex = projects.value.findIndex(p => p._id === projectId)
        if (projectIndex !== -1) {
          projects.value[projectIndex] = {
            ...projects.value[projectIndex],
            deleted: true,
            deletedAt: new Date().toISOString(),
            status: 'archived'
          }
        }
      }
      
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete project'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function restoreProject(projectId) {
    loading.value = true
    error.value = null
    try {
      const response = await api.post(`/projects/${projectId}/restore`)
      
      // Update project to show as restored
      const projectIndex = projects.value.findIndex(p => p._id === projectId)
      if (projectIndex !== -1) {
        projects.value[projectIndex] = response.data.project
      }
      
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to restore project'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchDeletedProjects() {
    loading.value = true
    error.value = null
    try {
      const response = await api.get('/projects/deleted/list')
      return response.data.deletedProjects || []
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to fetch deleted projects'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Admin functions for scheduler management
  async function getSchedulerStatus() {
    loading.value = true
    error.value = null
    try {
      const response = await api.get('/admin/scheduler/status')
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to get scheduler status'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function executeScheduledDeletions() {
    loading.value = true
    error.value = null
    try {
      const response = await api.post('/admin/scheduler/execute/deletions')
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to execute scheduled deletions'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function forceDeleteProject(projectId, reason) {
    loading.value = true
    error.value = null
    try {
      const response = await api.delete(`/admin/scheduler/force/${projectId}`, {
        data: { reason }
      })
      
      // Remove from projects list
      projects.value = projects.value.filter(p => p._id !== projectId)
      if (selectedProjectId.value === projectId) {
        selectedProjectId.value = null
        selectedProject.value = null
      }
      
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to force delete project'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    projects,
    selectedProjectId,
    selectedProject,
    loading,
    error,
    fetchProjects,
    createProject,
    getProjectDetails,
    selectProject,
    updateProject,
    deleteProject,
    restoreProject,
    fetchDeletedProjects,
    getSchedulerStatus,
    executeScheduledDeletions,
    forceDeleteProject
  }
})
